import { BaseButton, PayoutIcon } from '@/shared/components'
import { ItemLayout } from '@/shared/components/ItemLayout/ItemLayout'
import { formatCurrency } from '@/shared/lib/currency'
import { formatDate } from '@/shared/lib/date'
import { formatRequisite } from '@/shared/lib/requisite/formatRequisite'
import { getBankMeta } from '@/shared/utils/getBankMeta'
import { SortDirection, SortIcon } from '@/entities/sort'
import { ColumnDef } from '@tanstack/react-table'
import { PaidWithdrawModel, WithdrawStatusIcons, WithdrawStatusTitles } from '../model'
import { PayoutButton } from '@/features/withdraws'
import { FileText } from 'lucide-react'
import { getShortenId } from '@/shared/utils/getShortenId'
import { Link } from 'react-router-dom'
import { CopyBlock } from '@/shared/components/CopyBlock/CopyBlock'

export const getColumnsInProgress = ({
  disabled,
  onAction,
  sort,
  toggleSort,
  onManageReceipts,
}: {
  disabled: boolean
  onAction: (withdrawId: string) => void
  sort?: {
    sortCreatedAt?: SortDirection
    sortAmountTo?: SortDirection
    sortAllowedAt?: SortDirection
  }
  toggleSort?: (key: keyof NonNullable<typeof sort>) => void
  onManageReceipts?: (withdrawId: string) => void
}): ColumnDef<PaidWithdrawModel>[] => [
  {
    accessorKey: 'id',
    header: 'ID заявки',
    cell: ({ row }) => {
      const id = row.original.id
      return (
        <CopyBlock text={id} title={id}>
          <Link to={id} className="text-primary">
            {getShortenId(id)}
          </Link>
        </CopyBlock>
      )
    },
  },
  {
    accessorKey: 'payout',
    header: () => {
      return (
        <div className="flex items-center gap-1" onClick={() => toggleSort?.('sortCreatedAt')}>
          <div className="flex justify-between">
            <div className="flex items-center gap-1" onClick={() => toggleSort?.('sortCreatedAt')}>
              <span>Заявка</span>
              {sort && <SortIcon direction={sort.sortCreatedAt} />}
            </div>
            <div className="flex items-center gap-1" onClick={() => toggleSort?.('sortAllowedAt')}>
              <span>Осталось времени</span>
              <span className="shrink-0">{sort && <SortIcon direction={sort.sortAllowedAt} />}</span>
            </div>
          </div>
        </div>
      )
    },
    cell: ({ row }) => {
      return (
        <Link to={row.original.id}>
          <ItemLayout
            icon={<PayoutIcon status={WithdrawStatusIcons[row.original.withdrawStatus]} />}
            title={
              <span className="mr-[10px]">{WithdrawStatusTitles[row.original.withdrawStatus]}</span>
            }
            subtitle={formatDate(row.original.acceptedByTraderAt)}
            titleClasses="font-medium"
            timeLeft={row.original.payingAllowedTill}
            statusForTime="inProgress"
          />
        </Link>
      )
    },
  },
  {
    accessorKey: 'requisite',
    header: 'Реквизит',
    cell: ({ row }) => {
      const { title } = getBankMeta(row.original.bankToName)
      return (
        <div className="flex flex-col">
          <CopyBlock text={row.original.paymentRequisitValue}>
            {formatRequisite(row.original.paymentRequisitValue)}
          </CopyBlock>
          <span className="text-text-secondary">{title}</span>
        </div>
      )
    },
  },
  {
    accessorKey: 'amount',
    header: () => {
      return (
        <div className="flex items-center gap-1" onClick={() => toggleSort?.('sortAmountTo')}>
          <span>Сумма</span>
          {sort && <SortIcon direction={sort.sortAmountTo} />}
        </div>
      )
    },
    cell: ({ row }) => {
      return (
        <div className="flex flex-col">
          <CopyBlock text={row.original.amountTo.toFixed(2)}>
            {formatCurrency(row.original.amountTo, row.original.currencyTo)}
          </CopyBlock>
        </div>
      )
    },
  },
  {
    accessorKey: 'receipts',
    header: '',
    cell: ({ row }) => {
      return (
        <BaseButton
          padding="xs"
          variant="ghost"
          onClick={() => onManageReceipts?.(row.original.id)}
          icon={<FileText className="w-4 h-4" />}
          iconPosition="left"
        >
          Управление чеками
        </BaseButton>
      )
    },
  },
  {
    accessorKey: 'action',
    header: '',
    cell: ({ row }) => {
      return (
        <PayoutButton
          status="inProgress"
          onClick={() => onAction(row.original.id)}
          disabled={disabled}
        />
      )
    },
  },
]
